import { RfuSummary } from '@/types/pages/storeManagement/history';
import { Download } from '@mui/icons-material';
import { Typography, Box, Tooltip, IconButton, Alert } from '@mui/material';
import { find, size } from 'lodash';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

type FirmwareMacItem = {
    version: string;
    macs: string[];
};
type RfuContentProps = {
    summaryInfo: RfuSummary;
};

type Props = {
    items: FirmwareMacItem[];
};

type MacLine = { text: string; kind: 'mac' } | { text: string; kind: 'more' } | { text: string; kind: 'empty' };

const PANEL_MAX_HEIGHT = 500;
const DEVICE_STATUS_PANEL_WIDTH = 600;

const chunkArray = (arr: string[], size: number) => {
    const result: string[][] = [];

    for (let i = 0; i < arr.length; i += size) {
        result.push(arr.slice(i, i + size));
    }

    return result;
};

const FirmwareMacWrapTable: FC<Props> = ({ items }) => {
    const { t } = useTranslation('common');

    const maxLine = 2;
    const maxChunk = 3;

    const renderMacLines = (macs: string[]): MacLine[] => {
        if (macs.length === 0) {
            return [{ text: '—', kind: 'empty' }];
        }

        const chunks = chunkArray(macs, maxChunk);
        const visibleChunks = chunks.slice(0, maxLine);

        const lines: MacLine[] = visibleChunks.map((chunk) => ({
            text: chunk.join(', '),
            kind: 'mac',
        }));

        const remaining = Math.max(0, macs.length - maxChunk * maxLine);

        if (remaining > 0) {
            lines.push({ text: `... and ${remaining} mores.`, kind: 'more' });
        }

        return lines;
    };

    const downloadAsJson = (rows: FirmwareMacItem[], filename = 'firmware_macs.json') => {
        const json = JSON.stringify(rows, null, 2);
        const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();

        URL.revokeObjectURL(url);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                maxHeight: PANEL_MAX_HEIGHT,
                border: (t) => `1px solid ${t.palette.divider}`,
                borderRadius: 1,
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '150px 1fr',
                    bgcolor: 'action.hover',
                    px: 1,
                    py: 0.75,
                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                }}
            >
                <Typography variant="body2" fontWeight={700}>
                    {t('Firmware version')}
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                    {t('Mac addresses')}
                </Typography>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    overflow: 'auto',
                }}
            >
                {items.map(({ version, macs }, idx) => {
                    const lines = renderMacLines(macs);

                    return (
                        <Box
                            key={version}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '150px 1fr',
                                px: 1,
                                py: 0.75,
                                bgcolor: idx % 2 === 0 ? 'background.paper' : 'action.selected',
                                borderBottom: (t) => `1px solid ${t.palette.divider}`,
                            }}
                        >
                            <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {version}
                            </Typography>

                            <Box>
                                {lines.map((line, lineIdx) => (
                                    <Typography
                                        key={lineIdx}
                                        variant="body2"
                                        sx={{
                                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                            lineHeight: 1.6,
                                            color:
                                                line.kind === 'more'
                                                    ? 'text.secondary'
                                                    : line.kind === 'empty'
                                                      ? 'text.disabled'
                                                      : 'text.primary',
                                        }}
                                    >
                                        {line.text}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            <Box
                sx={{
                    borderTop: (t) => `1px solid ${t.palette.divider}`,
                    px: 1,
                    py: 0.5,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }}
            >
                <Tooltip title={t('Download as JSON')}>
                    <IconButton size="small" onClick={() => downloadAsJson(items)}>
                        <Download fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};
const RfuContent: FC<RfuContentProps> = ({ summaryInfo }) => {
    const { t } = useTranslation('common');

    if (summaryInfo.mode === 'explicit_targets') {
        const items = Object.values(summaryInfo.targetsByFw).flatMap((fwMap) =>
            Object.entries(fwMap).map(([version, macs]) => ({
                version,
                macs,
            })),
        );
        return <FirmwareMacWrapTable items={items} />;
    }
    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    alignItems: 'center',
                }}
            >
                {summaryInfo.upgrades.length > 0 && (
                    <Alert severity="info" sx={{ width: '100%', maxWidth: DEVICE_STATUS_PANEL_WIDTH }}>
                        {t('Firmware update has started. The device will continue updating in the background.')}
                    </Alert>
                )}
                {(() => {
                    const { upgrades, max_by_device, no_upgrades } = summaryInfo;
                    const formatAction = (deviceHex: string) => {
                        const upgradeInfo = find(upgrades, { device_type: deviceHex });
                        const noUpgradeInfo = find(no_upgrades, { device_type: deviceHex });
                        if (upgradeInfo) {
                            return t('Upgrade started to {{device}}{{version}}', {
                                device: upgradeInfo.device_type,
                                version: upgradeInfo.new_ver_hex,
                            });
                        } else if (noUpgradeInfo) {
                            return t('Already on latest version');
                        } else {
                            return '-';
                        }
                    };

                    return (
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: DEVICE_STATUS_PANEL_WIDTH,
                                border: (t) => `1px solid ${t.palette.divider}`,
                                borderRadius: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: PANEL_MAX_HEIGHT,
                                overflow: 'hidden',
                                mt: 2,
                            }}
                        >
                            {/* header */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '50px minmax(140px, 0.85fr) minmax(220px, 1.15fr)',
                                    columnGap: 2,
                                    px: 1,
                                    py: 0.5,
                                    alignItems: 'center',
                                    bgcolor: 'action.hover',
                                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                }}
                            >
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    #
                                </Typography>
                                <Box>
                                    <Typography
                                        variant="caption"
                                        sx={{ display: 'block', fontWeight: 600, lineHeight: 1.35 }}
                                    >
                                        {t('Current Store')}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ display: 'block', fontWeight: 600, lineHeight: 1.35 }}
                                    >
                                        {t('Firmware Versions')}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: 'normal' }}>
                                    {t('Action')}
                                </Typography>
                            </Box>

                            {/* rows */}
                            <Box
                                sx={{
                                    flex: 1,
                                    overflow: 'auto',
                                }}
                            >
                                {size(max_by_device) === 0 ? (
                                    <Typography variant="body2" color="text.secondary" textAlign={'center'}>
                                        {t('None')}
                                    </Typography>
                                ) : (
                                    <>
                                        {Object.entries(max_by_device).map(([deviceHex], idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns:
                                                        '50px minmax(140px, 0.85fr) minmax(220px, 1.15fr)',
                                                    columnGap: 2,
                                                    px: 1,
                                                    py: 0.5,
                                                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {idx + 1}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {`${deviceHex}`}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {formatAction(deviceHex)}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </>
                                )}
                            </Box>
                        </Box>
                    );
                })()}
            </Box>
        </>
    );
};
export default RfuContent;
